import { getToken } from './auth';

// 全局序列号计数器
let globalSequence = 0;

// 增强的UUID生成函数，确保高并发下的绝对唯一性
function generateUUID(): string {
  // 递增序列号，确保同一时刻的唯一性
  globalSequence = (globalSequence + 1) % 0xFFFF;
  
  // 获取高精度时间戳
  const timestamp = Date.now();
  const performanceTime = typeof performance !== 'undefined' ? performance.now() : 0;
  
  // 使用更强的随机源
  let randomBytes: Uint8Array;
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    randomBytes = new Uint8Array(16);
    crypto.getRandomValues(randomBytes);
  } else {
    // 备用方案：使用多个Math.random()增强随机性
    randomBytes = new Uint8Array(16);
    for (let i = 0; i < 16; i++) {
      randomBytes[i] = Math.floor(Math.random() * 256);
    }
  }
  
  // 将时间戳和序列号嵌入到UUID中
  const timestampHex = timestamp.toString(16).padStart(12, '0');
  const sequenceHex = globalSequence.toString(16).padStart(4, '0');
  const performanceHex = Math.floor(performanceTime * 1000).toString(16).padStart(8, '0');
  
  // 构建增强的UUID格式：时间戳-序列号-4xxx-性能时间-随机数
  const uuid = [
    timestampHex.substring(0, 8),
    sequenceHex,
    '4' + (randomBytes[6] & 0x0f).toString(16) + (randomBytes[7] & 0xff).toString(16).padStart(2, '0'),
    ((randomBytes[8] & 0x3f) | 0x80).toString(16) + (randomBytes[9] & 0xff).toString(16).padStart(2, '0'),
    performanceHex.substring(0, 4) + Array.from(randomBytes.slice(10, 16)).map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 8)
  ].join('-');
  
  return uuid;
}

// WebSocket连接状态枚举
export enum WebSocketStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  ERROR = 'error'
}

// 消息类型
export interface WebSocketMessage {
  id: string;
  content: any;
  timestamp: number;
  type: 'received' | 'sent';
}

// WebSocket事件监听器类型
export type WebSocketEventListener = (event: any) => void;
export type WebSocketMessageListener = (message: WebSocketMessage) => void;
export type WebSocketStatusListener = (status: WebSocketStatus) => void;

// WebSocket配置
export interface WebSocketConfig {
  url: string;
  autoReconnect?: boolean;
  reconnectInterval?: number;
  pingInterval?: number;
  pingMessage?: string | object;
  debug?: boolean;
  useCustomToken?: boolean;
  customToken?: string;
}

class WebSocketService {
  private static instance: WebSocketService;
  private ws: WebSocket | null = null;
  private status: WebSocketStatus = WebSocketStatus.DISCONNECTED;
  private config: WebSocketConfig = {
    url: '',
    autoReconnect: true,
    reconnectInterval: 5000,
    pingInterval: 30000,
    pingMessage: { type: 'ping', timestamp: 0 },
    debug: true,
    useCustomToken: false,
    customToken: ''
  };
  private pingIntervalId: NodeJS.Timeout | null = null;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  private messageListeners: WebSocketMessageListener[] = [];
  private statusListeners: WebSocketStatusListener[] = [];
  private messageHistory: WebSocketMessage[] = [];
  private maxMessageHistory: number = 100;
  private clientId: string | null = null;
  private lastInitConfig: string | null = null; // 记录上次初始化的配置

  // 私有构造函数，确保单例模式
  private constructor() {}

  // 获取单例实例
  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  // 初始化WebSocket配置
  public init(config: Partial<WebSocketConfig>): void {
    const newConfigStr = JSON.stringify(config);
    
    // 检查配置是否真正发生变化
    if (this.lastInitConfig === newConfigStr) {
      return; // 配置未变化，跳过初始化
    }
    
    this.config = { ...this.config, ...config };
    this.lastInitConfig = newConfigStr;
    this.log('WebSocket服务初始化完成');
  }

  // 连接WebSocket
  public connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      //this.log('WebSocket已经连接或正在连接中');
      return;
    }

    this.updateStatus(WebSocketStatus.CONNECTING);
    this.log('开始连接WebSocket...');

    try {
      // 获取token
      let token = '';
      if (this.config.useCustomToken && this.config.customToken) {
        token = this.config.customToken;
        this.log(`使用自定义token: ${token.substring(0, 20)}...`);
      } else {
        token = getToken() || '';
        this.log(`使用Cookie中的token: ${token ? token.substring(0, 20) + '...' : '无token'}`);
      }

      if (!token) {
        this.log('警告: 没有提供token，连接可能会被拒绝', 'warn');
      }

      // 构建WebSocket URL
      const url = this.config.url.includes('?') 
        ? `${this.config.url}&token=${encodeURIComponent(token)}` 
        : `${this.config.url}?token=${encodeURIComponent(token)}`;
      
      this.log(`连接WebSocket: ${url}`);
      
      // 创建WebSocket连接
      this.ws = new WebSocket(url);
      
      // 设置事件处理器
      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
    } catch (error) {
      this.updateStatus(WebSocketStatus.ERROR);
      this.log(`初始化WebSocket时出错: ${error instanceof Error ? error.message : String(error)}`, 'error');
      this.addMessage(`初始化WebSocket时出错: ${error instanceof Error ? error.message : String(error)}`, 'received');
    }
  }

  // 断开WebSocket连接
  public disconnect(): void {
    this.log('手动断开WebSocket连接');
    this.cleanup();
    this.updateStatus(WebSocketStatus.DISCONNECTED);
    this.addMessage('手动断开WebSocket连接', 'received');
  }

  // 发送消息
  public send(message: string | object): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.log('WebSocket未连接，无法发送消息', 'error');
      return false;
    }

    if (!this.clientId) {
      this.log('client_id未获取，无法发送消息', 'error');
      return false;
    }

    try {
      // 构建包含client_id的消息
      let messageToSend: any;
      if (typeof message === 'string') {
        try {
          const parsedMessage = JSON.parse(message);
          messageToSend = {
            ...parsedMessage,
            client_id: this.clientId
          };
        } catch {
          // 如果不是JSON字符串，直接包装
          messageToSend = {
            content: message,
            client_id: this.clientId
          };
        }
      } else {
        messageToSend = {
          ...message,
          client_id: this.clientId
        };
      }
      
      const msgStr = JSON.stringify(messageToSend);
      this.ws.send(msgStr);
      this.addMessage(message, 'sent');
      this.log(`发送消息: ${msgStr}`);
      return true;
    } catch (error) {
      this.log(`发送消息出错: ${error instanceof Error ? error.message : String(error)}`, 'error');
      return false;
    }
  }

  // 添加消息监听器
  public addMessageListener(listener: WebSocketMessageListener): () => void {
    this.messageListeners.push(listener);
    return () => this.removeMessageListener(listener);
  }

  // 移除消息监听器
  public removeMessageListener(listener: WebSocketMessageListener): void {
    this.messageListeners = this.messageListeners.filter(l => l !== listener);
  }

  // 添加状态监听器
  public addStatusListener(listener: WebSocketStatusListener): () => void {
    this.statusListeners.push(listener);
    listener(this.status); // 立即通知当前状态
    return () => this.removeStatusListener(listener);
  }

  // 移除状态监听器
  public removeStatusListener(listener: WebSocketStatusListener): void {
    this.statusListeners = this.statusListeners.filter(l => l !== listener);
  }

  // 获取当前状态
  public getStatus(): WebSocketStatus {
    return this.status;
  }

  // 获取消息历史
  public getMessageHistory(): WebSocketMessage[] {
    return [...this.messageHistory];
  }

  // 清除消息历史
  public clearMessageHistory(): void {
    this.messageHistory = [];
    this.notifyMessageListeners(null);
  }

  // 获取client_id
  public getClientId(): string | null {
    return this.clientId;
  }

  // WebSocket打开事件处理
  private handleOpen(event: Event): void {
    this.updateStatus(WebSocketStatus.CONNECTED);
    this.log('WebSocket连接已建立');
    this.addMessage('WebSocket连接已建立', 'received');
    
    // 设置定时发送心跳
    this.startPingInterval();
  }

  // WebSocket消息事件处理
  private handleMessage(event: MessageEvent): void {
    //console.log('收到消息event====>',event)
    try {
      const data = JSON.parse(event.data);
      //this.log(`收到消息: ${JSON.stringify(data)}`);
      
      // 处理连接成功消息，存储client_id
      if (data.type === 'connect' && data.data && data.data.client_id) {
        this.clientId = data.data.client_id;
        this.log(`存储client_id: ${this.clientId}`);
      }
      
      this.addMessage(data, 'received');
    } catch (error) {
      this.log(`收到非JSON消息: ${event.data}`);
      this.addMessage(event.data, 'received');
    }
  }

  // WebSocket关闭事件处理
  private handleClose(event: CloseEvent): void {
    this.updateStatus(WebSocketStatus.DISCONNECTED);
    const reason = event.reason || '未提供';
    this.log(`WebSocket连接已关闭: 代码=${event.code}, 原因=${reason}`);
    this.addMessage(`WebSocket连接已关闭: 代码=${event.code}, 原因=${reason}`, 'received');
    
    // 清理资源
    this.cleanup(false);
    
    // 如果配置了自动重连，则尝试重新连接
    if (this.config.autoReconnect) {
      this.scheduleReconnect();
    }
  }

  // WebSocket错误事件处理
  private handleError(event: Event): void {
    this.updateStatus(WebSocketStatus.ERROR);
    this.log('WebSocket连接错误', 'error');
    this.addMessage('WebSocket连接错误', 'received');
  }

  // 开始心跳定时器
  private startPingInterval(): void {
    if (this.pingIntervalId) {
      clearInterval(this.pingIntervalId);
    }
    
    this.pingIntervalId = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const pingMsg = typeof this.config.pingMessage === 'string' 
          ? this.config.pingMessage 
          : JSON.stringify({
              ...this.config.pingMessage,
              timestamp: Date.now()
            });
        
        this.ws.send(pingMsg);
        this.log(`发送心跳: ${pingMsg}`);
        this.addMessage(
          typeof this.config.pingMessage === 'string' 
            ? this.config.pingMessage 
            : { ...this.config.pingMessage, timestamp: Date.now() }, 
          'sent'
        );
      }
    }, this.config.pingInterval);
  }

  // 安排重新连接
  private scheduleReconnect(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
    }
    
    this.log(`计划在 ${this.config.reconnectInterval}ms 后重新连接...`);
    this.reconnectTimeoutId = setTimeout(() => {
      this.log('尝试重新连接...');
      this.addMessage('尝试重新连接...', 'received');
      this.connect();
    }, this.config.reconnectInterval);
  }

  // 清理资源
  private cleanup(closeWebSocket: boolean = true): void {
    // 清除定时器
    if (this.pingIntervalId) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
    
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    
    // 清除client_id
    this.clientId = null;
    
    // 关闭WebSocket连接
    if (closeWebSocket && this.ws) {
      try {
        this.ws.close();
      } catch (e) {
        // 忽略关闭错误
      }
      this.ws = null;
    }
  }

  // 更新状态并通知监听器
  private updateStatus(status: WebSocketStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.notifyStatusListeners();
    }
  }

  // 添加消息到历史记录并通知监听器
  private addMessage(content: any, type: 'received' | 'sent'): void {
    const message: WebSocketMessage = {
      id: generateUUID(),
      content,
      timestamp: Date.now(),
      type
    };
    
    this.messageHistory.push(message);
    
    // 限制历史记录长度
    if (this.messageHistory.length > this.maxMessageHistory) {
      this.messageHistory.shift();
    }
    
    this.notifyMessageListeners(message);
  }

  // 通知所有消息监听器
  private notifyMessageListeners(message: WebSocketMessage | null): void {
    if (message) {
      this.messageListeners.forEach(listener => {
        try {
          listener(message);
        } catch (e) {
          this.log(`消息监听器出错: ${e}`, 'error');
        }
      });
    } else {
      // 如果message为null，表示清除了历史记录
      this.messageListeners.forEach(listener => {
        try {
          listener({
            id: `clear-${Date.now()}`,
            content: 'history-cleared',
            timestamp: Date.now(),
            type: 'received'
          });
        } catch (e) {
          this.log(`消息监听器出错: ${e}`, 'error');
        }
      });
    }
  }

  // 通知所有状态监听器
  private notifyStatusListeners(): void {
    this.statusListeners.forEach(listener => {
      try {
        listener(this.status);
      } catch (e) {
        this.log(`状态监听器出错: ${e}`, 'error');
      }
    });
  }

  // 日志函数
  private log(message: string, level: 'log' | 'warn' | 'error' = 'log'): void {
    if (!this.config.debug) return;
    
    const prefix = '[WebSocketService]';
    switch (level) {
      case 'warn':
        console.warn(`${prefix} ${message}`);
        break;
      case 'error':
        console.error(`${prefix} ${message}`);
        break;
      default:
        console.log(`${prefix} ${message}`);
    }
  }
}

// 导出单例实例
export const websocketService = WebSocketService.getInstance();

// 默认导出
export default websocketService;